import { NovelItem_Status } from '@app/_proto/Protos/novels';
import type { PaymentMethodItem } from '@app/_proto/Protos/payments';
import {
    GetSponsorPlansRequest_SortDirection,
    GetSponsorPlansRequest_SortType,
    type SponsorPlanItem,
} from '@app/_proto/Protos/sponsors';
import type { SubscriptionItem } from '@app/_proto/Protos/subscriptions';
import { UnlockItemMethod, UnlockItemRequest } from '@app/_proto/Protos/unlocks';
import { UnlocksClient } from '@app/_proto/Protos/unlocks.client';
import { UpdateUserSettingsRequest } from '@app/_proto/Protos/users';
import { UsersClient } from '@app/_proto/Protos/users.client';
import { BoolValue } from '@app/_proto/google/protobuf/wrappers';
import { CHAPTER_EVENT_SOURCES, CHAPTER_UNLOCK_TYPES } from '@app/analytics/constants';
import { analyticsFactory } from '@app/analytics/handlers';
import KarmaPurchaseDialog from '@app/components/karma/karma.purchase.dialog';
import { CHAMPION_EVENT_SOURCES } from '@app/domains/champion/analytics/posthog/constants';
import { ChampionEvents } from '@app/domains/champion/analytics/posthog/events';
import ChampionPurchaseCompletedNovelDialog from '@app/domains/champion/components/ChampionPurchaseCompletedNovelDialog';
import ChampionPurchaseDialog from '@app/domains/champion/components/ChampionPurchaseDialog';
import useChampionPlans from '@app/domains/champion/hooks/useChampionPlans';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import ChapterKarmaPaywall from '@app/domains/chapter/components/ChapterKarmaPaywall';
import type { ChapterLockProps } from '@app/domains/chapter/components/ChapterLock';
import { ERROR_REASONS_TO_BUY_KARMA } from '@app/domains/chapter/constants/error';
import { checkVipFreeNovelSelectable } from '@app/domains/chapter/utils';
import useUnlocks from '@app/domains/common/hooks/useUnlocks';
import useUserStatus from '@app/domains/common/hooks/useUserStatus';
import { ToastMessageTypes, toastMessageFactory } from '@app/domains/common/utils/toast';
import useVipFreeNovels from '@app/domains/novel/hooks/useVipFreeNovels';
import { useGrpcApiWithRequest } from '@app/libs/api';
import { useAuth } from '@app/libs/auth';
import { GrpcError, getOneofValue, useGrpcRequest } from '@app/libs/grpc';
import { type ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

type KarmaChapterLockProps = ChapterLockProps & {
    canChapterUnlockByVoucher: boolean;
    availableVouchers: number;
};

export default function ChapterKarmaPaywallFetcher({
    novel,
    chapter,
    onUnlockChapter,
    onReloadChapter,
    canChapterUnlockByVoucher,
    availableVouchers,
}: KarmaChapterLockProps) {
    const { user, login, refreshUser } = useAuth();

    const { autoUnlockEnabled } = useUserStatus();
    const [autoUnlock, setAutoUnlock] = useState(false);

    const [karmaDialogOpen, setKarmaDialogOpen] = useState(false);
    const [championDialogOpen, setChampionDialogOpen] = useState(false);

    const [disabled, setDisabled] = useState(false);

    const chapterVoucherAmount = 1;

    const { activeSubscription: activeChampionSubscription, isLoading: isChampionSubscriptionLoading } =
        useChampionSubscriptions({
            novelId: novel.id,
        });

    const { data: championPlans, isLoading: isChampionPlansLoading } = useChampionPlans({
        novelId: novel.id,
        sortType: GetSponsorPlansRequest_SortType.AdvanceChapters,
        sortDirection: GetSponsorPlansRequest_SortDirection.ASC,
        showModalOnError: false,
    });

    const { data: unlockedNovels, isLoading: isUnlockedNovelsLoading } = useUnlocks({
        user: user ?? undefined,
        staleTime: 1000 * 60 * 60 * 24,
    });

    const { data: vipFreeNovels, isLoading: isVipFreeNovelsLoading } = useVipFreeNovels({ user: user ?? undefined });

    const handleCloseKarmaDialog = useCallback(() => setKarmaDialogOpen(false), []);

    const handleChangeAutoUnlock = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setAutoUnlock(event.target.checked);
    }, []);

    const handleClickSubscribeChampion = () => {
        if (!user) return login(window.location.pathname);
        setChampionDialogOpen(true);
    };

    const handleClickSubscribeChampionPlan = (plan: SponsorPlanItem) => {
        analyticsFactory(ChampionEvents.ClickChampionSubscribe)({
            on: CHAMPION_EVENT_SOURCES.KarmaPaywallLayer,
            novel,
            plan,
        });
    };

    const handleSubscribeChampionPlan = (subscription: SubscriptionItem, paymentMethod: PaymentMethodItem) => {
        const selectedPlan = getOneofValue(subscription.plan?.plan, 'sponsor') as SponsorPlanItem;
        analyticsFactory(ChampionEvents.BuyChampionSubscribe)({
            on: CHAMPION_EVENT_SOURCES.KarmaPaywallLayer,
            novel,
            subscription: activeChampionSubscription,
            plan: selectedPlan,
            paymentMethod,
        });
        setChampionDialogOpen(false);
        onReloadChapter();
    };

    const unlockKarmaRequest = useGrpcRequest(UnlockItemRequest, {
        item: {
            id: {
                oneofKind: 'chapterId',
                chapterId: chapter.entityId,
            },
        },
        unlockMethod: UnlockItemMethod.UnlockMethodKarma,
    });

    const unlockVoucherRequest = useGrpcRequest(UnlockItemRequest, {
        item: {
            id: {
                oneofKind: 'chapterId',
                chapterId: chapter.entityId,
            },
        },
        unlockMethod: UnlockItemMethod.UnlockMethodVoucher,
    });

    const updateUserSettingsRequest = useGrpcRequest(UpdateUserSettingsRequest, {
        autoUnlockChapters: BoolValue.create({
            value: autoUnlock,
        }),
    });

    const { executeRequest: executeKarmaRequest, loading: isKarmaLoading } = useGrpcApiWithRequest(
        UnlocksClient,
        c => c.unlockItem,
        unlockKarmaRequest
    );

    const { executeRequest: executeVoucherRequest, loading: isVoucherLoading } = useGrpcApiWithRequest(
        UnlocksClient,
        c => c.unlockItem,
        unlockVoucherRequest
    );

    const { executeRequest: executeUpdateUserSettings } = useGrpcApiWithRequest(
        UsersClient,
        c => c.updateUserSettings,
        updateUserSettingsRequest
    );

    const handleClickUnlockByKarma = useCallback(async () => {
        setDisabled(true);
        try {
            if (!user) {
                login(window.location.pathname);
                return;
            }

            const resp = await executeKarmaRequest();

            if (resp?.unlockedItem) {
                const karmaPrice = chapter?.karmaInfo?.karmaPrice?.value ?? 0;
                toastMessageFactory[ToastMessageTypes.CHAPTER_UNLOCKED_BY_KARMA]({ karmaPrice });

                onUnlockChapter?.({ on: CHAPTER_EVENT_SOURCES.KarmaLock, type: CHAPTER_UNLOCK_TYPES.Karma });

                if (user.settings?.autoUnlockChapters !== autoUnlock) {
                    await executeUpdateUserSettings();
                    refreshUser();
                }
            }
        } catch (error) {
            const errorDescription = error.response?.description ?? '';
            const errorReason = error.response?.metadata?.reason;
            if (errorDescription) {
                toast(errorDescription);
            }
            if (error instanceof GrpcError && ERROR_REASONS_TO_BUY_KARMA.includes(errorReason)) {
                setKarmaDialogOpen(true);
            }
        } finally {
            setDisabled(false);
        }
    }, [
        user,
        chapter?.karmaInfo?.karmaPrice?.value,
        executeKarmaRequest,
        login,
        onUnlockChapter,
        autoUnlock,
        executeUpdateUserSettings,
        refreshUser,
    ]);

    const handleUnlockByVoucher = useCallback(async () => {
        setDisabled(true);
        try {
            if (!user) {
                login(window.location.pathname);
                return;
            }

            const resp = await executeVoucherRequest();

            if (resp?.unlockedItem) {
                toastMessageFactory[ToastMessageTypes.CHAPTER_UNLOCKED_BY_VOUCHER]({
                    voucherAmount: chapterVoucherAmount,
                });

                await onUnlockChapter?.({
                    on: CHAPTER_EVENT_SOURCES.KarmaLock,
                    type: CHAPTER_UNLOCK_TYPES.Voucher,
                });

                if (user.settings?.autoUnlockChapters !== autoUnlock) {
                    await executeUpdateUserSettings();
                    refreshUser();
                }
            }
        } catch (error) {
            const errorDescription = error.response?.description ?? '';
            const errorReason = error.response?.metadata?.reason;
            if (errorDescription) {
                toast(errorDescription);
            }
        } finally {
            setDisabled(false);
        }
    }, [user, executeVoucherRequest, login, onUnlockChapter, autoUnlock, executeUpdateUserSettings, refreshUser]);

    const handlePurchaseComplete = useCallback(() => {
        setKarmaDialogOpen(false);
        handleClickUnlockByKarma();
    }, [handleClickUnlockByKarma]);

    const chapterKarmaPrice = chapter.karmaInfo?.karmaPrice?.value ?? 0;
    const canSubscribeChampion = !!(!activeChampionSubscription && novel.sponsorInfo?.hasAnyPlans?.value);
    const ChampionPurchaseDialogComponent =
        novel.status !== NovelItem_Status.Finished ? ChampionPurchaseDialog : ChampionPurchaseCompletedNovelDialog;

    return (
        <>
            <ChapterKarmaPaywall
                karmaPrice={chapterKarmaPrice}
                autoUnlockEnabled={!!autoUnlockEnabled}
                canChapterUnlockByVoucher={canChapterUnlockByVoucher}
                chapterVoucherAmount={chapterVoucherAmount}
                availableVouchers={availableVouchers}
                isLoading={
                    isChampionSubscriptionLoading ||
                    isChampionPlansLoading ||
                    isUnlockedNovelsLoading ||
                    isVipFreeNovelsLoading
                }
                isKarmaLoading={isKarmaLoading}
                isVoucherLoading={isVoucherLoading}
                disabled={disabled}
                shouldEncourageVipFreeNovelSelect={checkVipFreeNovelSelectable({
                    user: user ?? undefined,
                    novel,
                    vipFreeNovels,
                    unlockedNovels,
                })}
                shouldEncourageChampionSubscribe={canSubscribeChampion}
                onUnlockByKarma={handleClickUnlockByKarma}
                onUnlockByVoucher={handleUnlockByVoucher}
                onSubscribeChampion={handleClickSubscribeChampion}
                onChangeAutoUnlock={handleChangeAutoUnlock}
            />
            <KarmaPurchaseDialog
                open={karmaDialogOpen}
                onClose={handleCloseKarmaDialog}
                onSuccess={handlePurchaseComplete}
            />
            {championPlans && championPlans.length > 0 && championDialogOpen && (
                <ChampionPurchaseDialogComponent
                    on={CHAMPION_EVENT_SOURCES.KarmaPaywallLayer}
                    novelId={novel.id}
                    novelName={novel.name}
                    activeChampionSubscription={activeChampionSubscription}
                    initialView="benefit"
                    championPlans={championPlans}
                    onClickSubscribePlan={handleClickSubscribeChampionPlan}
                    onSubscribePlan={handleSubscribeChampionPlan}
                    onClose={() => {
                        setChampionDialogOpen(false);
                    }}
                />
            )}
        </>
    );
}
